<template>
  <Modal
    id="acquirer-history-modal"
    title="Histórico de alterações das adquirentes"
    :active="active"
    data-testId="acquirer_history_modal"
    @close="closeModal"
  >
    <div v-if="sections.length">
      <div
        v-for="section in sections"
        :key="section.id"
        class="mb-base"
        :data-testId="'acquirer_history_section_' + section.name"
      >
        <h5 class="mb-3">{{ section.name }}</h5>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th style="width: 18%">Data e Hora</th>
              <th style="width: 20%">Usuário</th>
              <th style="width: 18%">Campo</th>
              <th style="width: 22%">Valor Anterior</th>
              <th style="width: 22%">Novo Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!section.logs.length">
              <td colspan="5" class="text-center">
                {{ noHistoryFound }}
              </td>
            </tr>
            <tr
              v-for="log in section.logs"
              :key="log.id"
              :data-testId="'acquirer_history_row_' + log.id"
            >
              <td>{{ formatDate(log.createdAt) }}</td>
              <td>
                {{ log.user && log.user.name ? log.user.name : 'System' }}
              </td>
              <td>{{ translateFieldName(log.fieldName) }}</td>
              <td>{{ log.oldValue }}</td>
              <td>{{ log.newValue }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <p v-else data-testId="acquirer_history_empty">
      {{ noHistoryFound }}
    </p>
  </Modal>
</template>

<script>
import Modal from '../../components/Modal.vue';
import creditCardAcquirerApi from '../../services/credit.card.acquirer.api';
import functions from '../../utils/functions';
import notify from '@/utils/notify';
import { EMISSION_SYSTEMS } from '../../utils/constants';

const FIELD_NAME_TRANSLATIONS = Object.freeze({
  name: 'Adquirente',
  gateway_id: 'Gateway ID',
  advance_enabled: 'Antecipação',
  emission_systems: 'Sistemas de emissão',
  payment_type_id: 'Tipo de pagamento',
});

const ADVANCE_ENABLED_TRANSLATIONS = Object.freeze({
  true: 'Ativo',
  false: 'Inativo',
});

export default {
  name: 'AcquirerHistoryModal',
  components: { Modal },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    paymentTypeId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      sections: [],
      noHistoryFound: 'Nenhum histórico encontrado.',
    };
  },
  watch: {
    active(opened) {
      if (opened) {
        this.fetchHistory();
      } else {
        this.sections = [];
      }
    },
  },
  methods: {
    async fetchHistory() {
      if (this.paymentTypeId == null) return;
      this.$vs.loading();
      try {
        const acquirers = await creditCardAcquirerApi.getAcquirers(
          this.paymentTypeId,
        );
        const list = Array.isArray(acquirers) ? acquirers : [];
        this.sections = await Promise.all(
          list.map(async (acquirer) => {
            const logs = await creditCardAcquirerApi.getAcquirerLogs(
              acquirer.id,
            );
            return {
              id: acquirer.id,
              name: acquirer.name,
              logs: (Array.isArray(logs) ? logs : []).map((log) =>
                this.normalizeLog(log),
              ),
            };
          }),
        );
      } catch (error) {
        this.sections = [];
        notify.notifyFail(this.$vs, `Erro ao buscar os logs: ${error}`);
      } finally {
        this.$vs.loading.close();
      }
    },
    normalizeLog(log) {
      return {
        ...log,
        oldValue: this.formatValue(log.fieldName, log.oldValue),
        newValue: this.formatValue(log.fieldName, log.newValue),
      };
    },
    formatValue(fieldName, value) {
      if (value === null || value === undefined || value === '') return '-';

      if (fieldName === 'advance_enabled') {
        return ADVANCE_ENABLED_TRANSLATIONS[value] || value;
      }

      if (fieldName === 'emission_systems') {
        return this.formatEmissionSystems(value);
      }

      return value;
    },
    formatEmissionSystems(value) {
      let parsed;
      try {
        parsed = JSON.parse(value);
      } catch {
        return value;
      }

      if (!Array.isArray(parsed) || !parsed.length) return '-';

      return parsed
        .map((system) =>
          EMISSION_SYSTEMS[system] ? EMISSION_SYSTEMS[system].label : system,
        )
        .join(', ');
    },
    translateFieldName(fieldName) {
      if (!fieldName) return '-';
      return FIELD_NAME_TRANSLATIONS[fieldName] || fieldName;
    },
    formatDate(date) {
      if (!date) return '-';
      return functions.formatDatetimeWithMoment(date);
    },
    closeModal() {
      this.sections = [];
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.table-bordered {
  font-size: 1rem;
  display: table;
  border-collapse: separate;
  box-sizing: border-box;
  border-spacing: 2px;
  width: 100%;

  tr td {
    padding: 2px 2px 2px 10px;
  }
  tr:nth-child(2n) {
    background: #efefef !important;
  }
}
</style>

<style>
#acquirer-history-modal.con-vs-popup .vs-popup {
  width: fit-content !important;
  min-width: 70% !important;
}
</style>
